import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { agentAPI } from '@/lib/api';

/**
 * TransferAgentDialog — 2-step flow: form → confirm. Owns its form + step +
 * pending state. Parent only tracks open/close and refreshes on save.
 */
export const TransferAgentDialog = ({ open, onOpenChange, agent, agency, agencies = [], onTransferred }) => {
  const [step, setStep] = React.useState('form');
  const [targetId, setTargetId] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [notify, setNotify] = React.useState(true);
  const [pending, setPending] = React.useState(false);

  // Reset when the dialog closes so re-entry is always clean.
  React.useEffect(() => {
    if (!open) {
      setStep('form');
      setTargetId('');
      setReason('');
      setNotify(true);
      setPending(false);
    }
  }, [open]);

  const handleAdvance = async () => {
    if (step === 'form') {
      if (!targetId) return;
      setStep('confirm');
      return;
    }
    // step === 'confirm' → fire the transfer
    setPending(true);
    try {
      // Phase 27.3 — backend contract expects `new_agency_id` (see
      // AgentTransferRequest in server.py). The prior key
      // `target_agency_id` produced a 422 with a Pydantic error array in
      // `detail`, which sonner then tried to render as a React child →
      // ErrorBoundary crash.
      await agentAPI.transfer(agent.npn, {
        new_agency_id: targetId,
        reason: reason || null,
        notify_agent: notify,
      });
      toast.success('Agent transferred successfully');
      onOpenChange(false);
      onTransferred?.();
    } catch (err) {
      // Defensive: FastAPI validation errors return `detail` as an array
      // of objects. Always coerce to a plain string before handing to
      // sonner so a stray 422 never crashes the page.
      const raw = err?.response?.data?.detail;
      let message;
      if (typeof raw === 'string') message = raw;
      else if (Array.isArray(raw)) message = raw.map((e) => e?.msg || JSON.stringify(e)).join('; ');
      else if (raw && typeof raw === 'object') message = raw.msg || JSON.stringify(raw);
      else message = 'Transfer failed';
      toast.error(message);
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {step === 'confirm' ? 'Confirm Transfer' : 'Transfer Agent'}
          </DialogTitle>
        </DialogHeader>

        {step === 'form' ? (
          <div className="py-4 space-y-4" data-testid="transfer-form">
            <p className="text-sm text-slate-600">
              Transfer <strong>{agent?.first_name} {agent?.last_name}</strong> (NPN: {agent?.npn}) to a different agency.
            </p>
            <div className="space-y-2">
              <Label>New Agency <span className="text-red-500">*</span></Label>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger data-testid="transfer-agency-select"><SelectValue placeholder="Select agency" /></SelectTrigger>
                <SelectContent>
                  {agencies.filter((a) => a.agency_id !== agent?.agency_id).map((a) => (
                    <SelectItem key={a.agency_id} value={a.agency_id}>{'—'.repeat(a.depth || 0)} {a.agency_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Textarea
                placeholder="Reason for transfer..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                data-testid="transfer-reason-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={notify} onCheckedChange={setNotify} data-testid="transfer-notify-toggle" />
              <Label className="text-sm">Notify agent via email</Label>
            </div>
          </div>
        ) : (
          <div className="py-4" data-testid="transfer-confirm-step">
            <Alert className="mb-4 border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-sm">
                You are about to transfer this agent to another agency. This will
                update their hierarchy and reporting structure. All existing
                contracts and carrier appointments will be preserved. This action
                cannot be undone.
              </AlertDescription>
            </Alert>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between p-2 bg-slate-50 rounded">
                <span className="text-slate-500">Agent</span>
                <span className="font-medium">{agent?.first_name} {agent?.last_name}</span>
              </div>
              <div className="flex justify-between p-2 bg-slate-50 rounded">
                <span className="text-slate-500">From</span>
                <span className="font-medium">{agency?.agency_name || agent?.agency_id}</span>
              </div>
              <div className="flex justify-between p-2 bg-slate-50 rounded">
                <span className="text-slate-500">To</span>
                <span className="font-medium">{agencies.find((a) => a.agency_id === targetId)?.agency_name || targetId}</span>
              </div>
              {reason && (
                <div className="flex justify-between p-2 bg-slate-50 rounded">
                  <span className="text-slate-500">Reason</span>
                  <span className="font-medium text-right max-w-[60%]">{reason}</span>
                </div>
              )}
              <div className="flex justify-between p-2 bg-slate-50 rounded">
                <span className="text-slate-500">Notify Agent</span>
                <span className="font-medium">{notify ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 'confirm' && (
            <Button variant="outline" onClick={() => setStep('form')} disabled={pending} data-testid="transfer-back-btn">
              Back
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={handleAdvance}
            disabled={pending || (step === 'form' && !targetId)}
            variant={step === 'confirm' ? 'destructive' : 'default'}
            data-testid="transfer-confirm-btn"
          >
            {pending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
            {step === 'confirm' ? (pending ? 'Transferring...' : 'Confirm Transfer') : 'Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TransferAgentDialog;
